import React, { useState, useEffect } from 'react';
import { categoriesAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Toast from '../../components/common/Toast';
import IconPicker from '../../components/IconPicker';

const CategoryList = () => {
  const [type, setType] = useState('guidance');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [formData, setFormData] = useState({ id: '', name: '', icon: '', description: '', display_order: 0 });

  useEffect(() => {
    fetchCategories();
  }, [type]);

  const filteredCategories = categories.filter((cat) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (cat.name || '').toLowerCase().includes(q) ||
      (cat.id || '').toLowerCase().includes(q)
    );
  });

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = type === 'guidance' ? await categoriesAPI.getGuidanceCategories() : await categoriesAPI.getDuaCategories();
      if (response.data.success) setCategories(response.data.data.categories);
    } catch (error) {
      setToast({ show: true, message: 'Failed to fetch categories', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        if (type === 'guidance') await categoriesAPI.updateGuidanceCategory(editing.id, formData);
        else await categoriesAPI.updateDuaCategory(editing.id, formData);
        setToast({ show: true, message: 'Category updated', type: 'success' });
      } else {
        if (type === 'guidance') await categoriesAPI.createGuidanceCategory(formData);
        else await categoriesAPI.createDuaCategory(formData);
        setToast({ show: true, message: 'Category created', type: 'success' });
      }
      resetForm();
      fetchCategories();
    } catch (error) {
      setToast({ show: true, message: 'Failed to save', type: 'error' });
    }
  };

  const handleEdit = (cat) => {
    setEditing(cat);
    setFormData({ id: cat.id, name: cat.name, icon: cat.icon || '', description: cat.description || '', display_order: cat.display_order });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      if (type === 'guidance') await categoriesAPI.deleteGuidanceCategory(id);
      else await categoriesAPI.deleteDuaCategory(id);
      setToast({ show: true, message: 'Category deleted', type: 'success' });
      fetchCategories();
    } catch (error) {
      setToast({ show: true, message: error.response?.data?.message || 'Failed to delete', type: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({ id: '', name: '', icon: '', description: '', display_order: 0 });
    setEditing(null);
    setShowModal(false);
  };

  return (
    <div>
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div><h2 className="page-title">Categories</h2><p className="page-subtitle">Manage content categories</p></div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}><i className="bi bi-plus-circle me-2"></i>New Category</button>
        </div>
      </div>

      <div className="stat-card mb-4">
        <div className="row g-3 align-items-center">
          <div className="col-md-8">
            <div className="btn-group">
              <button className={`btn ${type === 'guidance' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setType('guidance')}>Guidance Categories</button>
              <button className={`btn ${type === 'duas' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setType('duas')}>Dua Categories</button>
            </div>
          </div>
          <div className="col-md-4">
            <label className="form-label">Search</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="data-table">
          <table className="table">
            <thead><tr><th>Name</th><th>ID</th><th>Icon</th><th>Order</th><th>Count</th><th className="text-end">Actions</th></tr></thead>
            <tbody>
              {filteredCategories.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                    <p className="text-muted mb-0">No categories found</p>
                  </td>
                </tr>
              ) : (
                filteredCategories.map(cat => (
                <tr key={cat.id}>
                  <td>{cat.name}</td>
                  <td><small className="text-muted">{cat.id}</small></td>
                  <td><i className={`bi bi-${cat.icon}`}></i></td>
                  <td>{cat.display_order}</td>
                  <td>{type === 'guidance' ? cat.article_count : cat.dua_count || 0}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(cat)}><i className="bi bi-pencil"></i></button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(cat.id)}><i className="bi bi-trash"></i></button>
                    </div>
                  </td>
                </tr>
              ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header"><h5 className="modal-title">{editing ? 'Edit' : 'New'} Category</h5><button className="btn-close" onClick={resetForm}></button></div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3"><label className="form-label">ID *</label><input type="text" className="form-control" value={formData.id} onChange={(e) => setFormData({ ...formData, id: e.target.value })} required disabled={!!editing} /></div>
                  <div className="mb-3"><label className="form-label">Name *</label><input type="text" className="form-control" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
                  <div className="mb-3"><label className="form-label">Icon</label><IconPicker value={formData.icon} onChange={(icon) => setFormData({ ...formData, icon })} /></div>
                  {type === 'guidance' && <div className="mb-3"><label className="form-label">Description</label><textarea className="form-control" rows="2" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}></textarea></div>}
                  <div className="mb-3"><label className="form-label">Display Order</label><input type="number" className="form-control" value={formData.display_order} onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })} /></div>
                </div>
                <div className="modal-footer"><button type="button" className="btn btn-outline-secondary" onClick={resetForm}>Cancel</button><button type="submit" className="btn btn-primary">{editing ? 'Update' : 'Create'}</button></div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryList;